import { Component, h, Prop, State, Watch, Event, EventEmitter, Element, Listen } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';

let uid = 0;

/**
 * COMPNAME is for creating an expanding/collapsing accordion component.
 *
 * An accordion can be set to "expanded" by default.
 */
@Component({
  tag: 'aafp-accordion',
  shadow: false,
})
export class AafpAccordion {
  /**
   * Text for the heading/button of the accordion
   */
  @Prop() heading!: string;
  /**
   * The heading sematic element type
   */
  @Prop() headingType: string = 'h2';
  /**
   * Set heading weight (light,normal,bold)
   */
  @Prop() headingWeight: string = '';
  /**
   * Set heading color
   */
  @Prop() headingColor: string = '';
  /**
   * Optional accordion anchor ID. Opens accordion when targeted
   */
  @Prop() accordionAnchor: string = '';
  /**
   * Whether the accordion should be open by default
   */
  @Prop() openByDefault: boolean = false;
  /**
   * Tracks the ID of the current open accordion (within its group)
   */
  @Prop() activeitem: string;
  /**
   * The random ID of this accordion
   */
  @Prop() componentId: string = `aafp-accordion-${uid++}`;

  @Element() element: HTMLElement;
  /**
   * Track state of this accordion (open or closed)
   */
  @State() expanded: boolean = this.openByDefault;

  // Emit an event when an accordion is toggled
  @Event() accordionToggled: EventEmitter<string>;
  accordionToggledHandler(idx: string) {
    this.accordionToggled.emit(idx);
  }

  @Listen('keydown')
  handleKeyDown(ev: KeyboardEvent) {
    if (ev.key === 'Escape') {
      this.toggle(false);
    }
  }

  // when the active accordion changes,
  // do a reset if necessary
  @Watch('activeitem')
  watchActiveitemHandler(newValue: string) {
    this.reset(newValue);
  }

  /**
   * Toggle this accordion and optionally let the
   * parent accordion group know the active accordion ID
   *
   * @param emit
   */
  toggle(emit: boolean = true) {
    // emit event up to the accordion group
    if (emit) {
      this.accordionToggledHandler(this.componentId);
    }
    this.expanded = !this.expanded;
  }

  /**
   * Reset this accordion if it's expanded
   * AND is not the active one
   * @param idx
   */
  reset(idx: string) {
    if (idx !== this.componentId && this.expanded) {
      this.toggle(false);
    }
  }

  /**
   * Toggle this accordion if it contains a targeted anchor
   * AND it is not already open
   */
  checkAnchor() {
    if (window.location.hash !== '' && this.expanded === false) {
      const target = document.querySelector(window.location.hash);

      if (window.location.hash == '#' + this.accordionAnchor) {
        this.toggle();
        target?.scrollIntoView();
      } else {
        if (target?.closest('#' + this.componentId)) {
          this.toggle();
          target.scrollIntoView();
        }
      }
    }
  }

  @Listen('hashchange', { target: 'window' })
  handleHashChange() {
    this.checkAnchor();
  }

  componentDidLoad() {
    this.checkAnchor();
  }

  render() {
    const HeadingTag = this.headingType;
    // const headingColor = (this.headingColor) ? "color:"+this.headingColor+";" : "";
    return (
      <div class="adl-accordion__item">
        <div
          class={getClassListString(
            {
              'adl-accordion__item--expanded': this.expanded,
            },
            '',
          )}
        >
          <span class="adl-accordion__anchor" id={this.accordionAnchor}></span>
          <div class="adl-accordion__title">
            <button
              aria-controls={this.componentId}
              aria-expanded={this.expanded ? 'true' : 'false'}
              class="adl-accordion__btn"
              type="button"
              onClick={() => this.toggle()}
            >
              <HeadingTag
                style={{ color: this.headingColor }}
                class={getClassListString(
                  {
                    'adl-weight-normal': this.headingWeight === 'normal',
                    'adl-weight-bold': this.headingWeight === 'bold',
                  },
                  'adl-accordion__heading',
                )}
              >
                {this.heading}
              </HeadingTag>
              <span class="material-icons" style={{ color: this.headingColor }} aria-hidden="true">
                expand_more
              </span>
            </button>
          </div>

          {/*accordion contents*/}
          <div
            id={this.componentId}
            class={getClassListString(
              {
                'adl-accordion__content--expanded': this.expanded,
              },
              'adl-accordion__content',
            )}
          >
            <aafp-collapsible expanded={this.expanded}>
              <slot />
            </aafp-collapsible>
          </div>
        </div>
      </div>
    );
  }
}
